import type { Store } from '@reduxjs/toolkit';
import type { Exception } from '@sentry/core';
import { get } from 'es-toolkit/compat';

import { config } from '../../config';
import { reportErrorToSentry } from '../setup/sentry';
import { trace } from '../setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { displayMessagesFetched, localeChanged } from './langSlice';

const sendError = (exception: Exception) => {
    reportErrorToSentry(exception, {
        tags: {
            module: config.sentryModuleName,
        },
    });
};

// Webpack is weird sometimes, maybe it's Babel, who knows...
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const normalizeDynamicImport = (imported: any) => get(imported, 'default', imported);

export const importDisplayMessages = (locale: string) =>
    import(`../../features/translations/${locale}.json`).then(normalizeDynamicImport).catch(error => {
        sendError(error);
        return error;
    });

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        } catch (error: any) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
